/**
 * IC Bingo — Audio Service
 *
 * Synthesizes premium sound effects via the Web Audio API (no external
 * audio files needed). All FX are:
 *   - Short (<1s) so they never interfere with the show pace.
 *   - Bright and elegant (major intervals, soft envelopes).
 *   - Silent when `setMuted(true)`.
 *
 * Chrome/Safari require a user interaction to unlock AudioContext.
 * Call `unlockOnUserGesture()` on mount to auto-resume the context on
 * the first user click / keypress / touch.
 */

class AudioService {
  constructor() {
    this.ctx = null;
    this.muted = false;
    this.unlocked = false;
    this.masterVolume = 0.8;
    this.sfxEnabled = true;
  }

  setMuted(muted) {
    this.muted = !!muted;
  }

  isMuted() {
    return this.muted;
  }

  setMasterVolume(v) {
    const n = Number(v);
    this.masterVolume = Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.8;
  }

  setSfxEnabled(enabled) {
    this.sfxEnabled = !!enabled;
  }

  _silent() {
    return this.muted || !this.sfxEnabled;
  }

  _ensureCtx() {
    if (!this.ctx && typeof window !== "undefined") {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) this.ctx = new AC();
    }
    return this.ctx;
  }

  unlockOnUserGesture() {
    if (this.unlocked || typeof window === "undefined") return;
    const unlock = () => {
      const ctx = this._ensureCtx();
      if (ctx && ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      this.unlocked = true;
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
  }

  _tone(ctx, start, freq, duration, type = "sine", gain = 0.18) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(gain * this.masterVolume, start + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration);
  }

  /**
   * playReveal — the standard cinematic reveal sound for every called number.
   * Design: bright bell + higher shimmer, ~700ms total, elegant fall-off.
   */
  playReveal() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // Fundamental — bright bell (E5)
    this._tone(ctx, t, 659.25, 0.55, "sine", 0.18);
    // Fifth above — richness (B5)
    this._tone(ctx, t + 0.06, 987.77, 0.5, "sine", 0.13);
    // High shimmer overtone (G6)
    this._tone(ctx, t + 0.02, 1567.98, 0.35, "triangle", 0.07);
  }

  /**
   * playPre — soft "up-swell" during the pre-game cinematic transition.
   */
  playPre() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(660, t + 2.2);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.14, t + 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.3);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 2.4);
  }

  /**
   * playWinner — celebratory chord (major triad, C5 E5 G5) for winner
   * announcements. Longer, richer than a normal reveal.
   */
  playWinner() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    this._tone(ctx, t, 523.25, 1.2, "sine", 0.16); // C5
    this._tone(ctx, t + 0.05, 659.25, 1.1, "sine", 0.13); // E5
    this._tone(ctx, t + 0.1, 783.99, 1.0, "sine", 0.11); // G5
    this._tone(ctx, t + 0.15, 1046.5, 0.7, "triangle", 0.07); // C6 shimmer
  }

  // ============================================================
  // Special number signature sounds. Each is 2–3s max.
  // ============================================================

  /** 7 — Lucky Seven: bright electric pulse (3 fast pings + shimmer) */
  playSpecial7() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // three fast bright pings
    [0, 0.08, 0.16].forEach((d) =>
      this._tone(ctx, t + d, 1174.66, 0.14, "square", 0.09),
    );
    // rising synth tail
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(400, t + 0.2);
    osc.frequency.exponentialRampToValueAtTime(1600, t + 1.6);
    g.gain.setValueAtTime(0, t + 0.2);
    g.gain.linearRampToValueAtTime(0.08, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t + 0.2);
    osc.stop(t + 2);
  }

  /** 13 — Unlucky Thirteen: low bass rumble + minor tolling bell */
  playSpecial13() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // deep bass sub
    const bass = ctx.createOscillator();
    const bg = ctx.createGain();
    bass.type = "sine";
    bass.frequency.setValueAtTime(55, t);
    bg.gain.setValueAtTime(0, t);
    bg.gain.linearRampToValueAtTime(0.18, t + 0.2);
    bg.gain.exponentialRampToValueAtTime(0.0001, t + 2.2);
    bass.connect(bg);
    bg.connect(ctx.destination);
    bass.start(t);
    bass.stop(t + 2.3);
    // minor bell — F#4
    this._tone(ctx, t + 0.15, 185, 1.2, "sine", 0.12);
    this._tone(ctx, t + 0.9, 220, 1.0, "sine", 0.1); // A3
  }

  /** 21 — Key of the Door: warm golden bell + soft ring */
  playSpecial21() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    this._tone(ctx, t, 440, 1.4, "sine", 0.16); // A4
    this._tone(ctx, t + 0.2, 554.37, 1.2, "sine", 0.12); // C#5
    this._tone(ctx, t + 0.4, 659.25, 1.0, "triangle", 0.08); // E5
  }

  /** 22 — Two Little Ducks: playful two-tone descending duo */
  playSpecial22() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    this._tone(ctx, t, 880, 0.35, "triangle", 0.14); // A5 "quack 1"
    this._tone(ctx, t + 0.05, 660, 0.4, "triangle", 0.11);
    this._tone(ctx, t + 0.55, 784, 0.35, "triangle", 0.14); // G5 "quack 2"
    this._tone(ctx, t + 0.6, 587.33, 0.4, "triangle", 0.11);
  }

  /** 45 — Halfway There: rhythmic double-note (heartbeat) */
  playSpecial45() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    this._tone(ctx, t, 220, 0.28, "sine", 0.14); // thump
    this._tone(ctx, t + 0.22, 220, 0.28, "sine", 0.14);
    this._tone(ctx, t + 0.6, 659.25, 0.9, "sine", 0.12); // resolve
    this._tone(ctx, t + 0.65, 987.77, 0.7, "sine", 0.09);
  }

  /** 69 — Favourite: swirling whoosh + soft chime */
  playSpecial69() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // swoop
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(900, t + 0.9);
    osc.frequency.exponentialRampToValueAtTime(300, t + 1.5);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.12, t + 0.2);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 1.9);
    // gentle chime at the end
    this._tone(ctx, t + 1.3, 987.77, 0.6, "sine", 0.1);
  }

  /** 77 — Sunset Strip: soulful long warm note + shimmer */
  playSpecial77() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    this._tone(ctx, t, 349.23, 2.2, "sine", 0.16); // F4 warm
    this._tone(ctx, t + 0.15, 440, 2.0, "sine", 0.12); // A4
    this._tone(ctx, t + 0.35, 523.25, 1.6, "triangle", 0.08); // C5
    this._tone(ctx, t + 0.8, 1046.5, 0.8, "triangle", 0.06); // shimmer
  }

  /** 88 — Two Fat Ladies: double bell chime + rich harmonics */
  playSpecial88() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // first bell
    this._tone(ctx, t, 622.25, 1.2, "sine", 0.16); // D#5
    this._tone(ctx, t + 0.02, 1244.5, 0.9, "triangle", 0.08);
    // second bell (delayed)
    this._tone(ctx, t + 0.35, 739.99, 1.2, "sine", 0.16); // F#5
    this._tone(ctx, t + 0.37, 1479.98, 0.9, "triangle", 0.08);
  }

  /** 90 — Grand Finale: orchestral swell + deep boom */
  playSpecial90() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // deep boom
    const boom = ctx.createOscillator();
    const bg = ctx.createGain();
    boom.type = "sine";
    boom.frequency.setValueAtTime(65, t);
    bg.gain.setValueAtTime(0, t);
    bg.gain.linearRampToValueAtTime(0.24, t + 0.06);
    bg.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
    boom.connect(bg);
    bg.connect(ctx.destination);
    boom.start(t);
    boom.stop(t + 1.5);
    // orchestral major chord swell
    this._tone(ctx, t + 0.1, 261.63, 2.3, "sine", 0.15); // C4
    this._tone(ctx, t + 0.15, 329.63, 2.2, "sine", 0.13); // E4
    this._tone(ctx, t + 0.2, 392.0, 2.0, "sine", 0.12); // G4
    this._tone(ctx, t + 0.25, 523.25, 1.8, "sine", 0.1); // C5
    this._tone(ctx, t + 0.3, 1046.5, 1.4, "triangle", 0.07); // C6 shimmer
  }

  /**
   * Phase 2 · Doubles signature — two-note twin chime with a shimmering
   * high harmonic. Distinct enough to be recognizable as "doubles" from
   * the first tone. Total length ~0.9s.
   */
  playDoubles() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // First ding — C6
    this._tone(ctx, t, 1046.5, 0.4, "sine", 0.18);
    this._tone(ctx, t + 0.005, 2093, 0.28, "triangle", 0.09);
    // Twin ding — E6 (major third up), same shimmer
    this._tone(ctx, t + 0.22, 1318.5, 0.45, "sine", 0.18);
    this._tone(ctx, t + 0.225, 2637, 0.32, "triangle", 0.09);
    // Soft after-glow harmonic
    this._tone(ctx, t + 0.42, 1568, 0.35, "sine", 0.08); // G6
  }

  /**
   * Phase 2 · Six Seven signature — two-beat "SIX ... SEVEN!" rhythm.
   *   Beat 1  0.00s  low bass thud (announces "SIX")
   *   pause   0.35s  suspense breath
   *   Beat 2  0.55s  rising chord POP (announces "SEVEN!") with sparkle
   * Total ~1.1s.
   */
  playSixSeven() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;

    // Beat 1 — low tom thud for "SIX"
    const thud = ctx.createOscillator();
    const tg = ctx.createGain();
    thud.type = "sine";
    thud.frequency.setValueAtTime(110, t); // A2
    thud.frequency.exponentialRampToValueAtTime(55, t + 0.28);
    tg.gain.setValueAtTime(0, t);
    tg.gain.linearRampToValueAtTime(0.28 * this.masterVolume, t + 0.02);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    thud.connect(tg);
    tg.connect(ctx.destination);
    thud.start(t);
    thud.stop(t + 0.45);

    // Beat 2 — bright rising chord POP for "SEVEN!"
    const t2 = t + 0.55;
    this._tone(ctx, t2, 523.25, 0.5, "sine", 0.2); // C5
    this._tone(ctx, t2 + 0.02, 659.26, 0.5, "sine", 0.18); // E5
    this._tone(ctx, t2 + 0.04, 783.99, 0.5, "sine", 0.16); // G5
    this._tone(ctx, t2 + 0.06, 1046.5, 0.45, "triangle", 0.12); // C6
    // Sparkle tail
    this._tone(ctx, t2 + 0.2, 2093, 0.35, "triangle", 0.08); // C7
  }

  /**
   * Phase 2 · Shy Ball signature — a demure two-beat coo.
   *   Beat 1  0.00s  a soft high whistle that dips (the "peek")
   *   Beat 2  0.55s  a gentle two-note bell resolve (the "reveal")
   *   Tail   1.10s   airy shimmer that fades out
   * Total ~1.4s. Rose-blush warm palette, never brassy.
   */
  playShyBall() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;

    // Beat 1 — shy whistle that peeks up then dips down
    const peek = ctx.createOscillator();
    const pg = ctx.createGain();
    peek.type = "sine";
    peek.frequency.setValueAtTime(660, t); // E5
    peek.frequency.exponentialRampToValueAtTime(880, t + 0.14);
    peek.frequency.exponentialRampToValueAtTime(494, t + 0.5);
    pg.gain.setValueAtTime(0, t);
    pg.gain.linearRampToValueAtTime(0.14 * this.masterVolume, t + 0.08);
    pg.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
    peek.connect(pg);
    pg.connect(ctx.destination);
    peek.start(t);
    peek.stop(t + 0.6);

    // Beat 2 — soft rose-bell resolve
    const t2 = t + 0.55;
    this._tone(ctx, t2, 523.25, 0.55, "sine", 0.15); // C5
    this._tone(ctx, t2 + 0.06, 659.26, 0.55, "sine", 0.13); // E5
    this._tone(ctx, t2 + 0.12, 987.77, 0.45, "triangle", 0.09); // B5 shimmer

    // Airy tail
    this._tone(ctx, t2 + 0.35, 1568, 0.4, "sine", 0.06); // G6 fade
  }

  /**
   * ===== Bingo Check · Heartbeat Verification =====
   *
   * A calm, cinematic verification mode triggered when a player claims
   * BINGO. The room naturally hushes as a steady heartbeat pulses in
   * the background. Each verified number gets a soft green confirmation
   * chime. The heartbeat stops INSTANTLY the moment the final number
   * is confirmed — that hard silence is what cues the celebration.
   *
   * The heartbeat is scheduled as a repeating "lub-dub" pattern via
   * setInterval. It's low-frequency and gentle, never overwhelming.
   */
  startHeartbeat(bpm = 60) {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    this.stopHeartbeat(); // guard against double-start

    const periodMs = 60000 / bpm;

    const doBeat = () => {
      if (this._silent()) return;
      const c = this._ensureCtx();
      if (!c || c.state === "suspended") return;
      const t = c.currentTime;
      // "Lub" — deep low thump
      this._heartTone(c, t, 55, 0.14, 0.28);
      // "Dub" — softer, slightly higher, ~130ms later
      this._heartTone(c, t + 0.13, 78, 0.13, 0.22);
    };

    // Fire the first beat immediately so the room hears the pulse start.
    doBeat();
    this._heartbeatTimer = setInterval(doBeat, periodMs);
  }

  stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
  }

  /**
   * setHeartbeatBpm — live-update the heartbeat cadence WITHOUT
   * dropping a beat. Silently no-ops if the heartbeat isn't running.
   */
  setHeartbeatBpm(bpm) {
    if (!this._heartbeatTimer) return;
    this.startHeartbeat(bpm); // startHeartbeat calls stopHeartbeat first
  }

  /**
   * _heartTone — one heartbeat blip. Deep sine with a soft body-blow
   * envelope so it feels felt-in-the-chest rather than heard-with-ears.
   */
  _heartTone(ctx, start, freq, duration, gain) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, start);
    // Very short attack, gentle body, quick decay
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(gain * this.masterVolume, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  }

  /**
   * playCheckConfirm — gentle green confirmation ding for one verified
   * number. Warm two-note major-third chime, ~450ms, low volume so it
   * sits UNDER the heartbeat rather than shouting over it.
   *
   * `isFinal=true` layers a slightly brighter resolve tone to signal
   * "that was the last one" — but the celebration itself is handled by
   * the Confirmed Bingo module that follows.
   */
  playCheckConfirm(isFinal = false) {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // Warm major-third (E5 → G#5) — feels affirmative, not celebratory
    this._tone(ctx, t, 659.26, 0.4, "sine", 0.16);
    this._tone(ctx, t + 0.06, 830.61, 0.4, "sine", 0.13);
    // A quiet high shimmer to add "sparkle" without triumph
    this._tone(ctx, t + 0.12, 1318.51, 0.3, "triangle", 0.06);

    if (isFinal) {
      // A soft resolve to the tonic (A5) — subtle, not fanfare
      this._tone(ctx, t + 0.2, 880, 0.55, "sine", 0.14);
    }
  }

  /**
   * ===== Confirmed Bingo Celebration · Hybrid =====
   *
   * A two-part celebration:
   *   Part 1  playIntimateChord()  — delicate warm string chord that
   *           carries the tender "Congratulations, {name}" reveal.
   *   Part 2  playCelebrationBrass() — bright brass fanfare + shimmer
   *           that kicks off the full confetti + fireworks moment.
   *
   * They're independent so the parent overlay can schedule the exact
   * hybrid pacing (silence → intimate → build → burst).
   */
  playIntimateChord() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // Warm F-major-7 voicing (F4 / A4 / C5 / E5) — tender, elegant
    this._softString(ctx, t, 349.23, 1.4, 0.13); // F4
    this._softString(ctx, t + 0.02, 440.0, 1.4, 0.11); // A4
    this._softString(ctx, t + 0.04, 523.25, 1.4, 0.1); // C5
    this._softString(ctx, t + 0.06, 659.26, 1.4, 0.09); // E5
    // Airy shimmer up top
    this._tone(ctx, t + 0.4, 1046.5, 0.9, "sine", 0.05); // C6
  }

  _softString(ctx, start, freq, duration, gain) {
    // Two detuned triangles for a mild string chorus feel
    const mkOsc = (detune) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(freq, start);
      osc.detune.setValueAtTime(detune, start);
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(gain * this.masterVolume, start + 0.25);
      g.gain.setValueAtTime(gain * this.masterVolume, start + duration - 0.35);
      g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + duration + 0.05);
    };
    mkOsc(-4);
    mkOsc(+4);
  }

  playCelebrationBrass() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t = ctx.currentTime;
    // Fanfare — bright ascending arpeggio, warm sawtooth+square for
    // brass character. C5 → E5 → G5 → C6 → E6 in ~0.8s, then chord.
    const arp = [
      [523.25, 0.0], // C5
      [659.26, 0.11],
      [783.99, 0.22], // G5
      [1046.5, 0.35], // C6
      [1318.51, 0.5], // E6
    ];
    arp.forEach(([f, dt]) => {
      this._brassNote(ctx, t + dt, f, 0.45, 0.14);
    });
    // Sustained chord after the arpeggio lands
    const t2 = t + 0.6;
    this._brassNote(ctx, t2, 523.25, 1.1, 0.12);
    this._brassNote(ctx, t2 + 0.02, 659.26, 1.1, 0.11);
    this._brassNote(ctx, t2 + 0.04, 783.99, 1.1, 0.1);
    // Sparkle tail
    this._tone(ctx, t2 + 0.2, 2093, 0.8, "triangle", 0.07); // C7
    this._tone(ctx, t2 + 0.4, 2637, 0.6, "triangle", 0.05); // E7
  }

  _brassNote(ctx, start, freq, duration, gain) {
    // sawtooth for body + square for bite, both softly enveloped
    const mk = (type, g) => {
      const osc = ctx.createOscillator();
      const gn = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, start);
      gn.gain.setValueAtTime(0, start);
      gn.gain.linearRampToValueAtTime(g * this.masterVolume, start + 0.04);
      gn.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      osc.connect(gn);
      gn.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + duration + 0.02);
    };
    mk("sawtooth", gain);
    mk("square", gain * 0.35);
  }

  /**
   * ===== Positive Energy · Energy Boost =====
   *
   * A short, upbeat rhythm loop that re-energizes the room after the
   * Confirmed Bingo Celebration. 120 BPM · 4 bars · kick / clap /
   * hi-hat + a melodic hook. Never chaotic — enough groove to get
   * hands clapping without turning the room into a nightclub.
   */
  playEnergyBoost() {
    if (this._silent()) return;
    const ctx = this._ensureCtx();
    if (!ctx || ctx.state === "suspended") return;
    const t0 = ctx.currentTime;
    const beat = 0.5; // 120 BPM
    const bar = beat * 4;
    const bars = 4;

    for (let b = 0; b < bars; b++) {
      const barT = t0 + b * bar;
      // Kicks on 1 and 3
      this._kick(ctx, barT);
      this._kick(ctx, barT + beat * 2);
      // Claps on 2 and 4
      this._clap(ctx, barT + beat);
      this._clap(ctx, barT + beat * 3);
      // Hi-hats on every half-beat (8 per bar)
      for (let hh = 0; hh < 8; hh++) {
        this._hihat(ctx, barT + (hh * beat) / 2);
      }
      // Melodic hook — plays first note of each bar for a bright riff
      const hookNotes = [
        [523.25, 0.0], // C5
        [659.26, beat * 0.5], // E5
        [783.99, beat * 1], // G5
        [1046.5, beat * 1.5], // C6
      ];
      hookNotes.forEach(([f, dt]) => {
        this._energyBleep(ctx, barT + dt + beat * 2, f, 0.24, 0.06);
      });
    }
    // Final punch
    this._kick(ctx, t0 + bars * bar);
    this._tone(ctx, t0 + bars * bar, 1046.5, 0.6, "triangle", 0.09);
  }

  _kick(ctx, start) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(120, start);
    osc.frequency.exponentialRampToValueAtTime(45, start + 0.09);
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.4 * this.masterVolume, start + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.2);
  }

  _clap(ctx, start) {
    // Two quick filtered noise bursts for a body-hit clap
    for (let i = 0; i < 2; i++) {
      const t = start + i * 0.012;
      const buf = ctx.createBuffer(1, 0.05 * ctx.sampleRate, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let j = 0; j < data.length; j++) {
        data[j] = (Math.random() * 2 - 1) * (1 - j / data.length);
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.setValueAtTime(1400, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.18 * this.masterVolume, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      src.connect(hp);
      hp.connect(g);
      g.connect(ctx.destination);
      src.start(t);
      src.stop(t + 0.12);
    }
  }

  _hihat(ctx, start) {
    const buf = ctx.createBuffer(1, 0.04 * ctx.sampleRate, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let j = 0; j < data.length; j++) {
      data[j] = (Math.random() * 2 - 1) * (1 - j / data.length);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.setValueAtTime(7000, start);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.05 * this.masterVolume, start);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.04);
    src.connect(hp);
    hp.connect(g);
    g.connect(ctx.destination);
    src.start(start);
    src.stop(start + 0.05);
  }

  _energyBleep(ctx, start, freq, duration, gain) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(freq, start);
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(gain * this.masterVolume, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  }
}

export const audioService = new AudioService();
